import {toast} from 'react-toastify';
import {takeEvery, put, select, call} from 'redux-saga/effects';
import {INIT_AI_UPDATE, setLoading, updateSlideData} from '../actions/course.actions';
import {selectSlideItemEntities} from '../selectors/course.selectors';
import {i18n} from '~/i18n';
import {retryFetch} from '@snapper/core';
import {createApi} from '@snapper/ai-core';
import {backendUrlV2} from '~/config';
import axios from 'axios';
import {getPages} from '~/stores/player/selectors/course.selectors';
import {mergeDeep} from 'immutable';

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const axiosInstance = axios.create();

export const SnapperAI = createApi({
  baseRequest: axiosInstance,
  baseUrl: backendUrlV2,
});

const callEditText = async ({
  text,
  command,
  prompt,
  ...rest
} = {}) => {
  const getData = async () => {
    const {promise, cancel} = SnapperAI.editText({
      text,
      command,
      custom_instructions: prompt,
    });

    return await promise;
  };

  return await retryFetch({
    fetchFn: getData,
    onFail: error => {
      console.log('callEditText failed', {error});
    },
  });
};

const callGetImagePrompt = async ({textContent, artStyle}) => {
  const getPrompt = async () => {
    const {promise, cancel} = SnapperAI.generateImagePromptFromText({
      textContent,
      context: {artStyle},
    });

    return await promise;
  };

  return await retryFetch({fetchFn: getPrompt});
};

// '1024', '1024x1792', '1792x1024'
const callCreateImage = async ({
  textContent,
  artStyle,
  size = '1536x1024',
  prompt: argsPrompt,
  image,
} = {}) => {
  const getImage = async () => {
    const prompt = argsPrompt || await callGetImagePrompt({textContent, artStyle});

    const {promise, cancel} = SnapperAI.createImage({prompt, size, image});

    return await promise;
  };

  return await retryFetch({
    fetchFn: getImage,
    onFail: error => {
      console.log('callCreateImage failed', {error});
    },
  });
};

export const cleanUploadedFileUrl = url => {
  if (typeof url !== 'string') return null;

  return url
    .replace('/files/', '/files/file/')
    .replace('/files/file/file/', '/files/file/')
    .replace('/download', '');
};

export const uploadFromBlob = async ({
  file: propsFile,
  blobUrl,
  displayName,
  fileName,
  categoryTitle,
}) => {
  if (!blobUrl && !propsFile) return null;

  let file = propsFile;

  if (!file) {
    const blob = await fetch(blobUrl).then(r => r.blob());

    file = new File([blob], fileName || 'illustration.jpg', {type: blob.type});
  }

  const formData = new FormData();

  formData.append('data', file);
  formData.append('display_name', displayName || 'illustration');

  if (categoryTitle) {
    formData.append('category_title', categoryTitle);
  }

  const {data: {url: uploadedImageUrl, id} = {}} = await axiosInstance({
    method: 'POST',
    url: `${backendUrlV2}/files/`,
    data: formData,
    headers: {'Content-Type': 'multipart/form-data'},
  });

  const finalUrl = cleanUploadedFileUrl(uploadedImageUrl);

  return {
    url: finalUrl,
    id,
  };
};

function* onInitAIUpdate(action) {
  const {
    id: itemId,
    pageId,
    item: payloadItem,
    text,
    slideTextContent,
    /** The AI action to perform, e.g., 'rewrite', 'summarize' */
    action: AIAction,
    args,
    onSuccess,
  } = action?.payload || {};

  try {
    const item = payloadItem || (yield select(selectSlideItemEntities))[itemId];

    if (!itemId && !item) {
      throw new Error('Item ID or item data is missing');
    }

    yield put(setLoading({id: itemId, isLoading: true}));

    let updatedValues;

    if (AIAction?.includes?.('image')) {
      const {
        artStyle = 'realistic photography',
        endpoint = 'create', // 'create' | 'edit' | 'variations'
      } = args || {};

      const base64Url = yield call(callCreateImage, {
        textContent: slideTextContent,
        artStyle,
        endpoint,
        ...args,
      });

      const fileName = `image-${Date.now()}.webp`;
      const res = yield uploadFromBlob({blobUrl: base64Url, fileName});

      updatedValues = {data: {file: {url: res?.url, name: fileName}}};
    } else {
      const updatedText = yield call(callEditText, {
        text,
        command: AIAction,
        ...args,
      });

      updatedValues = {data: {text: updatedText}};
    }

    if (updatedValues) {
      const prevPage = (yield select(getPages))?.find(page => page.id === pageId);
      const prevItems = prevPage?.pictureslides?.slides?.[0]?.items || [];
      const prevItem = prevItems.find(item => item.id === itemId);

      if (prevItem) {
        yield put(updateSlideData({
          pageId,
          data: {
            items: prevItems.map(item => {
              if (item?.id === itemId) {
                return mergeDeep(item, updatedValues);
              }

              return item;
            }),
          },
        }));
      }

      if (typeof onSuccess === 'function') {
        onSuccess(updatedValues);
      }
    }
  } catch (error) {
    toast.error(i18n('error.ai-error'));
    console.error(error);
  } finally {
    yield put(setLoading({id: itemId, isLoading: false}));
  }
}

export const sagas = [
  takeEvery(INIT_AI_UPDATE, onInitAIUpdate),
];
